import { ChangeDetectionStrategy, Component, input, output } from "@angular/core";
import { S25LoadingInlineStaticComponent } from "../../../../../../modules/s25-loading/s25.loading.inline.static.component";
import { S25PipesModule } from "../../../../../../pipes/s25.pipes.module";
import { FindLocationResultsLang, SpaceAvailabilityItem } from "./find-location.types";

@Component({
    selector: "s25-ng-dash-available-space-find-location-results",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            padding-top: 0.5rem;
            border-top: 1px solid var(--color--border);
            color: var(--color--text);
        }

        .results-list {
            list-style: none;
            margin: 0;
            padding: 0;
            background-color: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            color: var(--color--text);
        }

        .results-item {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            padding: 0.35rem 0;
        }

        .results-item + .results-item {
            border-top: 1px solid var(--color--border);
        }

        .results-item__header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.75rem;
        }

        .results-item__name {
            font-weight: bold;
        }

        .results-item__use {
            flex-shrink: 0;
            font-weight: bold;
            font-size: 0.9em;
            text-align: right;
        }

        .results-item__details {
            color: var(--color--text);
            font-size: 0.9em;
            line-height: 1.45;
        }

        .results-desc {
            background-color: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            color: var(--color--text);
            padding: 0.75rem;
        }

        .capacity-nav {
            display: flex;
            gap: 1em;
            padding-top: 0.5em;
        }

        .capacity-nav__side {
            flex: 1;
        }

        .capacity-nav__side--next {
            text-align: right;
        }

        .capacity-nav__side--next-only {
            text-align: center;
        }
    `,
    imports: [S25LoadingInlineStaticComponent, S25PipesModule],
    template: `
        @if (items().length) {
            <ul class="results-list" [attr.id]="lang().list.style.body">
                @for (spaceItem of items(); track spaceItem.itemId) {
                    <li class="results-item" [class]="lang().list.style.item">
                        <div class="results-item__header">
                            <a
                                class="text-main results-item__name"
                                tabindex="0"
                                (click)="viewLocation.emit(spaceItem)"
                                (keydown.enter)="viewLocation.emit(spaceItem)"
                            >
                                {{ spaceItem.itemName }}
                            </a>
                            <a
                                class="text-main results-item__use"
                                tabindex="0"
                                (click)="useSpace.emit(spaceItem)"
                                (keydown.enter)="useSpace.emit(spaceItem)"
                            >
                                {{ lang().list.text.use_this_location }}
                            </a>
                        </div>
                        <div class="results-item__details">
                            @if (spaceItem.itemDesc) {
                                <div>{{ spaceItem.itemDesc }}</div>
                            }
                            <div>
                                {{ lang().list.text.max_capacity }}
                                {{ spaceItem.maxCapacity }}
                            </div>
                        </div>
                    </li>
                }
            </ul>
        }

        @if (loaded()) {
            <div class="results-desc" [attr.id]="lang().desc.style" tabindex="0">
                <div [innerHTML]="descriptionHtml() | safeHTML"></div>
                <div class="capacity-nav">
                    @if (hasPrevCapacity()) {
                        <a
                            class="text-main capacity-nav__side"
                            tabindex="0"
                            (click)="prevCapacity.emit()"
                            (keydown.enter)="prevCapacity.emit()"
                        >
                            {{ lang().desc.text.check_smaller_capacity }}
                        </a>
                    }
                    <div
                        class="capacity-nav__side capacity-nav__side--next"
                        [class.capacity-nav__side--next-only]="!hasPrevCapacity()"
                    >
                        @if (hasNextCapacity()) {
                            <a
                                class="text-main"
                                tabindex="0"
                                (click)="nextCapacity.emit()"
                                (keydown.enter)="nextCapacity.emit()"
                            >
                                {{ lang().desc.text.check_larger_capacity }}
                            </a>
                        } @else {
                            <span>{{ lang().desc.text.no_larger_capacity }}</span>
                        }
                    </div>
                </div>
            </div>
        } @else if (loading()) {
            <s25-ng-loading-inline-static [text]="lang().loading.text" />
        }

        <a
            class="text-main"
            tabindex="0"
            [attr.id]="lang().try_advanced_search.style"
            (click)="advancedSearch.emit()"
            (keydown.enter)="advancedSearch.emit()"
        >
            {{ lang().try_advanced_search.text }}
        </a>
    `,
})
export class HomeDashAvailableSpaceFindLocationResultsComponent {
    readonly lang = input.required<FindLocationResultsLang>();
    readonly items = input<SpaceAvailabilityItem[]>([]);
    readonly loaded = input(false);
    readonly loading = input(false);
    readonly hasPrevCapacity = input(false);
    readonly hasNextCapacity = input(false);
    readonly descriptionHtml = input("");

    readonly viewLocation = output<SpaceAvailabilityItem>();
    readonly useSpace = output<SpaceAvailabilityItem>();
    readonly prevCapacity = output<void>();
    readonly nextCapacity = output<void>();
    readonly advancedSearch = output<void>();
}
